import type { LucideIcon } from 'lucide-react';
import { ChevronRight } from 'lucide-react';
import type { ReactNode } from 'react';

interface MeRowProps {
  icon: LucideIcon;
  title: string;
  sub?: string;
  danger?: boolean;
  /** Slot direito customizável — padrão é chevron */
  children?: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  'aria-label'?: string;
}

/**
 * Linha genérica de configuração/perfil.
 * Touch target ≥ 44px via minHeight.
 * Padding: 14px 16px conforme spec.
 */
export function MeRow({
  icon: Icon,
  title,
  sub,
  danger,
  children,
  onClick,
  disabled,
  'aria-label': ariaLabel,
}: MeRowProps) {
  const iconBg = danger ? 'var(--block-soft, var(--warn-soft))' : 'var(--card-soft)';
  const iconColor = danger ? 'var(--block)' : 'var(--fg-muted)';
  const titleColor = danger ? 'var(--block)' : 'var(--fg)';

  const inner = (
    <>
      <span
        style={{
          width: 34,
          height: 34,
          borderRadius: 10,
          background: iconBg,
          color: iconColor,
          display: 'grid',
          placeItems: 'center',
          flexShrink: 0,
        }}
        aria-hidden
      >
        <Icon size={18} strokeWidth={1.7} />
      </span>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 14,
            fontWeight: 500,
            color: disabled ? 'var(--fg-dim)' : titleColor,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {title}
        </div>
        {sub && (
          <div
            style={{
              fontSize: 11,
              fontFamily: 'var(--font-mono)',
              color: 'var(--fg-muted)',
              marginTop: 1,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {sub}
          </div>
        )}
      </div>

      {/* Slot direito */}
      {children !== undefined ? (
        children
      ) : (
        <ChevronRight size={16} style={{ color: 'var(--fg-dim)', flexShrink: 0 }} aria-hidden />
      )}
    </>
  );

  if (onClick) {
    return (
      <button
        type="button"
        onClick={disabled ? undefined : onClick}
        disabled={disabled}
        aria-label={ariaLabel ?? title}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '14px 0',
          width: '100%',
          textAlign: 'left',
          background: 'none',
          borderTop: 'none',
          borderLeft: 'none',
          borderRight: 'none',
          borderBottom: '1px solid var(--card-stroke)',
          cursor: disabled ? 'not-allowed' : 'pointer',
          minHeight: 44,
          opacity: disabled ? 0.5 : 1,
        }}
      >
        {inner}
      </button>
    );
  }

  return (
    <div
      role="listitem"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '14px 0',
        borderBottom: '1px solid var(--card-stroke)',
        minHeight: 44,
      }}
    >
      {inner}
    </div>
  );
}
